import { incomeTaxYear, GST_REGISTRATION } from "@stb/jurisdiction-au/income-tax"

import { displayYear, formatCheckedOn } from "../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../components/landing"
import { DEFAULT_FY } from "../components/landing/pay-calculator-model"
import { SoleTraderCalculator } from "../components/landing/sole-trader-calculator"
import {
  answerSentence,
  DEFAULT_SOLE_FORM,
  evaluateSole,
} from "../components/landing/sole-trader-calculator-model"
import type { Guide } from "./guide-types"

/**
 * Served at /sole-trader-tax-calculator (T916), not under /guides. Copy source:
 * docs/design/html/flows/web-landing-tool-sole-trader.mjs and docs/specs/pay-calculator-spec.md section 5.
 * Every figure comes from the rule table or the engine; the page only picks the examples.
 */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const checked = formatCheckedOn(year.resident.source.checkedOn)
const gstChecked = formatCheckedOn(GST_REGISTRATION.source.checkedOn)
const capChecked = formatCheckedOn(year.concessionalCap.source.checkedOn)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const cents = (bp: number) => `${bp / 100}c`
const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(bp / 100)}%`
const whole = (v: Parameters<typeof formatAud>[0]) => formatAud(v).replace(/\.00$/, "")

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
const h2 = "text-2xl font-semibold"
const para = "max-w-[70ch]"

const brackets = year.resident.brackets
const bracketRows = brackets.map((b, i) => {
  const next = brackets[i + 1]
  const range = next
    ? `${dollars(i === 0 ? 0 : b.from + 1)} – ${dollars(next.from)}`
    : `${dollars(b.from + 1)} and over`
  const tax =
    b.rateBp === 0
      ? "Nil"
      : b.baseDollars === 0
        ? `${cents(b.rateBp)} for each A$1 over ${dollars(b.from)}`
        : `${dollars(b.baseDollars)} plus ${cents(b.rateBp)} for each A$1 over ${dollars(b.from)}`
  return { header: range, cells: [tax] }
})

/** Profit examples: the engine, resident, no wages, no HELP, Medicare levy on. */
const PROFITS = [30_000, 60_000, 90_000, 120_000, 180_000]
const profitRows = PROFITS.flatMap((profit) => {
  const o = evaluateSole({ ...DEFAULT_SOLE_FORM, income: String(profit), expenses: "0" })
  if (o.kind !== "ok") return []
  const r = o.result
  return [
    {
      header: dollars(profit),
      cells: [
        whole(r.business.total),
        formatAud(r.setAside.perMonth),
        whole(r.takeHome),
        percent(r.setAside.shareOfProfitBp),
      ],
    },
  ]
})

const startYear = Number(DEFAULT_FY.slice(0, 4))
const previousFy = displayYear(`${startYear - 1}-${String(startYear).slice(2)}`)

const FAQ_PROFIT = 80_000
const faqOutcome = evaluateSole({ ...DEFAULT_SOLE_FORM, income: String(FAQ_PROFIT), expenses: "0" })
const faqExample = faqOutcome.kind === "ok" ? faqOutcome : undefined

const example = evaluateSole(DEFAULT_SOLE_FORM)
const exampleResult = example.kind === "ok" ? example : undefined

export const soleTraderGuide: Guide = {
  slug: "sole-trader-tax-calculator",
  title: `Sole Trader Tax Calculator Australia ${fy}`,
  description: `Free sole trader tax calculator for Australia, ${fy}: how much ABN income to set aside for tax each month, what you keep, and an employee comparison.`,
  crumb: "Sole trader tax calculator",
  h1: `Sole trader tax calculator: how much to set aside (${fy})`,
  published: "2026-11-02",
  reviewed: "2026-11-02",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO, tax rates for Australian residents", href: year.resident.source.url },
    { label: "ATO, registering for GST", href: GST_REGISTRATION.source.url },
  ],
  answer: answerSentence(DEFAULT_SOLE_FORM, formatAud),
  answerMore: (
    <>
      The calculator takes your ABN income and expenses, adds any wages, and works out the tax,
      Medicare levy and HELP repayment your business adds, using ATO rates for {fy}. It shows what
      to put aside each month or quarter and what you keep. It also compares you with an employee on
      the same pay. Enter your own numbers below.
    </>
  ),
  related: [
    { label: "Pay calculator: take-home pay for employees", href: "/pay-calculator" },
    { label: "GST calculator: add GST to a price or take it out", href: "/gst-calculator" },
    { label: "Cents per km calculator: your car expense claim", href: "/cents-per-km-calculator" },
    { label: "Free invoice generator: make a tax invoice", href: "/invoice-generator" },
    { label: "Invoicing for sole traders: send invoices, see who has paid", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "Work out what to set aside" },
    { id: "how", label: "How sole trader tax works" },
    { id: "examples", label: "Examples by profit" },
    { id: "rates", label: `Tax rates ${fy}` },
    { id: "gst", label: "GST and the A$75,000 threshold" },
    { id: "super", label: "Super for sole traders" },
    { id: "employee", label: "ABN or employee" },
    { id: "faq", label: "Common questions" },
  ],
  cta: {
    title: "Know your profit before tax time",
    description:
      "Kestly keeps your invoices, receipts and trips in one place, so your income and expenses are ready when you work out what to set aside.",
    ctaLabel: "Start free",
    ctaHref: "/login?from=sole-trader-tax-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-3 md:gap-4">
        <h2 id="calculator" className={h2}>
          Work out what to set aside for tax
        </h2>
        <SoleTraderCalculator />
      </section>

      <section aria-labelledby="how" className="grid gap-3">
        <h2 id="how" className={h2}>
          How sole trader tax works
        </h2>
        <p className={para}>
          A sole trader pays income tax on business profit, which is business income less deductible
          expenses. The profit is added to any other income, such as wages, and taxed at the same
          rates as everyone else. Medicare levy and any HELP repayment are worked out on the total.
        </p>
        <p className={para}>
          Clients generally pay an ABN invoice in full, so the tax is yours to put aside. The
          calculator takes the tax on your wages and profit together, subtracts the tax on your
          wages alone, and shows the difference as the tax your business adds. Divide it by 12 or 4
          for a monthly or quarterly amount.
        </p>
        {exampleResult && (
          <p className={para}>
            Example: {whole(exampleResult.annualIncome)} of income and{" "}
            {whole(exampleResult.expenses)} of expenses leaves {whole(exampleResult.result.profit)}{" "}
            of profit. Tax and Medicare levy come to {whole(exampleResult.result.business.total)} in{" "}
            {fy}, so you keep {whole(exampleResult.result.takeHome)}, and set aside{" "}
            {formatAud(exampleResult.result.setAside.perMonth)} a month.
          </p>
        )}
        <p className={para}>
          If the ATO sends you PAYG instalment notices, pay the amounts on them. This calculator
          does not work those out; it shows the tax for the whole year.
        </p>
        <SourceLine
          sources={[
            { label: "ATO, tax rates for Australian residents", href: year.resident.source.url },
          ]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="examples" className="grid gap-3">
        <h2 id="examples" className={h2}>
          Tax to set aside at different profits, {fy}
        </h2>
        <p className={para}>
          For an Australian resident with no wages and no HELP debt, the Medicare levy included.
          Your own numbers are in the calculator above.
        </p>
        <SimpleTable
          caption={`Tax and take-home on a sole trader profit, ${fy}`}
          columns={[
            "Profit",
            "Tax and levy a year",
            "Set aside a month",
            "You keep",
            "Share of profit",
          ]}
          rows={profitRows}
        />
        {year.medicare.status === "provisional" && (
          <p className="text-muted-foreground max-w-[70ch] text-sm">
            The ATO has not yet published the {fy} Medicare levy thresholds, so these figures use
            the {previousFy} thresholds.
          </p>
        )}
        <SourceLine
          sources={[
            { label: "ATO tax rates", href: year.resident.source.url },
            { label: "ATO low income tax offset", href: year.lito.source.url },
            { label: "ATO Medicare levy reduction", href: year.medicare.source.url },
          ]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className={h2}>
          Income tax rates {fy}: the same in every state
        </h2>
        <p className={para}>
          Income tax is federal, so a sole trader in NSW, VIC, QLD, WA, SA, TAS, the ACT or the NT
          pays the same. Most people also pay the {percent(year.medicare.rateBp)} Medicare levy;
          people on low incomes pay less or none.
        </p>
        <SimpleTable
          caption={`Resident income tax rates ${fy}, not including the Medicare levy`}
          columns={["Taxable income", `Tax on this income (${fy})`]}
          rows={bracketRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: year.resident.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="gst" className="grid gap-3">
        <h2 id="gst" className={h2}>
          GST and the {dollars(GST_REGISTRATION.thresholdDollars)} threshold
        </h2>
        <p className={para}>
          You must register for GST when your GST turnover (gross business income minus GST) is{" "}
          {dollars(GST_REGISTRATION.thresholdDollars)} or more, or when you start a business and
          expect to reach it in your first year. Taxi, limousine and ride-sourcing drivers must
          register whatever their turnover. Below the threshold registering is optional.
        </p>
        <p className={para}>
          The calculator uses income before GST, because GST you collect is not your income. Use the{" "}
          <a href="/gst-calculator" className={linkClass}>
            GST calculator
          </a>{" "}
          to add GST to a quote.
        </p>
        <SourceLine
          sources={[{ label: "ATO, registering for GST", href: GST_REGISTRATION.source.url }]}
          checked={gstChecked}
        />
      </section>

      <section aria-labelledby="super" className="grid gap-3">
        <h2 id="super" className={h2}>
          Super for sole traders
        </h2>
        <p className={para}>
          The calculator assumes no employer pays super on your business profit, so saving for
          retirement is your choice. Personal contributions you claim as a tax deduction count
          towards the general concessional cap, which is {dollars(year.concessionalCap.dollars)} in{" "}
          {fy} and includes any super an employer pays on your wages. The calculator does not
          include voluntary contributions, so your tax would be lower if you made one. If you are
          unsure whether your work counts as employment for super, ask the ATO.
        </p>
        <SourceLine
          sources={[{ label: "ATO, contributions caps", href: year.concessionalCap.source.url }]}
          financialYear={fy}
          checked={capChecked}
        />
      </section>

      <section aria-labelledby="employee" className="grid gap-3">
        <h2 id="employee" className={h2}>
          ABN or employee on the same pay
        </h2>
        <p className={para}>
          The table under the calculator compares your result with an employee who earns the same
          amount as a salary. The income tax is the same on the same taxable income. The differences
          are the expenses an ABN carries, and the employer super of{" "}
          {percent(year.superGuarantee.rateBp)} an employee gets on top. Paid leave and other
          entitlements are not valued.
        </p>
        <p className={para}>
          To compare two offers, enter the ABN rate above and set expenses to what you expect to
          spend, then compare it with the{" "}
          <a href="/pay-calculator" className={linkClass}>
            pay calculator
          </a>{" "}
          for the salary.
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "How much should a sole trader set aside for tax?",
      answer: `It depends on your profit. On ${dollars(FAQ_PROFIT)} of profit and nothing else, an Australian resident pays about ${faqExample ? whole(faqExample.result.business.total) : "the tax shown above"} including the Medicare levy in ${fy}, which is ${faqExample ? percent(faqExample.result.setAside.shareOfProfitBp) : "a share"} of profit. The calculator works out your own share from your income, expenses and any wages.`,
    },
    {
      question: "Do I pay the same tax rates as an employee?",
      answer:
        "Yes. Sole trader profit is taxed at the same resident rates as wages. What differs is that tax is not usually withheld from ABN payments, and the calculator assumes no employer pays super on your profit.",
    },
    {
      question: "Which expenses can I deduct?",
      answer:
        "The calculator takes one total for the year and does not check what is deductible. Ask the ATO or a registered tax agent about a specific cost. Kestly keeps your receipts and trips in one place for your records.",
    },
    {
      question: "What if my expenses are higher than my income?",
      answer:
        "The calculator shows no profit and no tax for the business, and does not set the loss against your wages. Whether a business loss can reduce other income depends on tests the calculator does not apply, so check with a registered tax agent.",
    },
    {
      question: "Do I add GST to my income?",
      answer: `No. Enter your income before GST. You must register for GST once your GST turnover reaches ${dollars(GST_REGISTRATION.thresholdDollars)}, and taxi and ride-sourcing drivers must register at any turnover. Source: ATO, registering for GST, checked ${gstChecked}.`,
    },
    {
      question: "Why does the calculator ask for weeks worked?",
      answer:
        "A weekly, daily or hourly rate is multiplied by the weeks you expect to work in the year. It starts at 52, so lower it if you plan time off.",
    },
    {
      question: "Is the small business income tax offset included?",
      answer:
        "No. Some sole traders can get a tax offset on their business income. The calculator leaves it out, so your real tax may be a little lower. The ATO works it out on your return.",
    },
    {
      question: "Does this replace the ATO's PAYG instalment notice?",
      answer:
        "No. If the ATO sends you PAYG instalment notices, pay the amounts on them. The calculator shows the tax for the whole year, not an instalment amount.",
    },
    {
      question: "Is this tax advice?",
      answer: `No. It is an estimate from the ATO rates for ${fy} and the assumptions listed under the result. For your own situation, check with the ATO or a registered tax agent.`,
    },
  ],
}
